import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../contexts/AuthContext';
import SafetyGuidanceDialog from '../components/auth/SafetyGuidanceDialog';
import HowItWorks from '../components/home/HowItWorks';
import SiteFooter from '../components/layout/SiteFooter';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SiteHeader, { HeaderButton } from '../components/layout/SiteHeader';
import LoginIcon from '@mui/icons-material/Login';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import landingBackground from '../assets/backgrounds/landingBackground.avif';
import { GOLDEN_MAJOR, GOLDEN_MINOR, PHI, TYPE_SCALE } from '../styles/golden';
import { BRAND, outlineButtonSx, primaryButtonSx } from '../styles/ui';

const Landing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [openSafetyModal, setOpenSafetyModal] = useState(false);
  const [safetyConfirmed, setSafetyConfirmed] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [highlightSafety, setHighlightSafety] = useState(false);

  useEffect(() => {
    if (location.hash === '#how-it-works') {
      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleGetStarted = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (email && email.includes('@')) {
      if (safetyConfirmed) {
        navigate('/register', { state: { email } });
      } else {
        setShowWarning(true);
        setHighlightSafety(true);
        // Set a timeout to stop the highlight animation after 2 seconds
        setTimeout(() => {
          setHighlightSafety(false);
        }, 2000);
      }
    }
  };

  const handleOpenSafetyModal = () => {
    setOpenSafetyModal(true);
  };

  const handleCloseSafetyModal = () => {
    setOpenSafetyModal(false);
    if (safetyConfirmed) {
      setShowWarning(false);
    }
  };

  const handleSafetyConfirmation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSafetyConfirmed(event.target.checked);
    if (event.target.checked) {
      setShowWarning(false);
    }
  };

  const secondaryActions = (highlight: boolean) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      <Button
        variant="outlined"
        component={RouterLink}
        to="/services"
        sx={{ ...outlineButtonSx, flex: '1 1 160px' }}
      >
        Local Support
      </Button>

      <Button
        onClick={handleOpenSafetyModal}
        variant="outlined"
        startIcon={<InfoOutlinedIcon />}
        sx={{
          ...outlineButtonSx,
          flex: '1 1 160px',
          borderColor: highlight ? BRAND.pink : BRAND.lineStrong,
          animation: highlight ? 'pulse 1s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(233, 83, 128, 0.7)' },
            '70%': { boxShadow: '0 0 0 10px rgba(233, 83, 128, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(233, 83, 128, 0)' }
          }
        }}
      >
        Safety Guidance
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', '@supports (min-height: 100dvh)': { minHeight: '100dvh' }, display: 'flex', flexDirection: 'column' }}>
      <SiteHeader
        actions={
          isAuthenticated ? (
            <HeaderButton to="/profile" icon={<PersonOutlineIcon />} iconOnlyOnPhone>My Profile</HeaderButton>
          ) : (
            <HeaderButton to="/login" icon={<LoginIcon />}>Log in</HeaderButton>
          )
        }
      />

      {/* Hero: the copy sits in the golden major part, over the darker side of the photo */}
      <Box
        component="main"
        sx={{
          flex: 1,
          // Desktop: the hero fills the first screen; the sections below follow on scroll
          minHeight: { md: 'calc(100vh - 64px)' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, sm: 5, lg: 6 },
          py: { xs: 5, md: 4 },
          backgroundColor: 'var(--joan-bg)',
          backgroundImage: {
            xs: `linear-gradient(${BRAND.veilFull}, ${BRAND.veilFull}), url(${landingBackground})`,
            md: `linear-gradient(to right, ${BRAND.veilStrong} ${GOLDEN_MINOR}, ${BRAND.veilSoft} 100%), url(${landingBackground})`
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Space above : below = 1 : φ puts the copy on the golden optical centre */}
        <Box sx={{ flex: { xs: 0, md: 1 } }} />

        <Box sx={{ width: { xs: '100%', md: GOLDEN_MAJOR }, maxWidth: 610 }}>
          <Typography
            variant="h1"
            sx={{ color: BRAND.ink, mb: 3, textShadow: BRAND.heroShadow }}
          >
            Raise the alarm.<br />
            Without making a sound.
          </Typography>
          <Typography
            component="p"
            sx={{
              color: BRAND.ink,
              fontSize: `${TYPE_SCALE.lead}rem`,
              mb: 5,
              textShadow: BRAND.heroShadow
            }}
          >
            Instant access to help when you need it the most
          </Typography>

          {!isAuthenticated ? (
            <Box sx={{ maxWidth: 520 }}>
              <Typography variant="h6" component="h2" sx={{ mb: 3, color: BRAND.ink, fontWeight: 400 }}>
                Ready to get started? Enter your email to create or restart your membership.
              </Typography>

              <Box
                component="form"
                onSubmit={handleGetStarted}
                noValidate
                sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2 }}
              >
                <TextField
                  fullWidth
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  inputProps={{ 'aria-label': 'Email address' }}
                  value={email}
                  onChange={handleEmailChange}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: BRAND.fieldFill, height: '100%' } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ ...primaryButtonSx, fontSize: '1.05rem', whiteSpace: 'nowrap', minWidth: { sm: 180 }, minHeight: 56 }}
                >
                  Get Started
                </Button>
              </Box>

              {showWarning && (
                <Typography variant="body2" role="alert" sx={{ color: BRAND.pink, mt: 2 }}>
                  Please visit the safety guidance before proceeding with an account creation
                </Typography>
              )}

              <Box sx={{ mt: 3 }}>{secondaryActions(highlightSafety)}</Box>
            </Box>
          ) : (
            <Box sx={{ maxWidth: 520 }}>
              {secondaryActions(false)}
            </Box>
          )}

          <Button
            component={RouterLink}
            to="/#how-it-works"
            endIcon={<ArrowDownwardIcon />}
            sx={{ mt: 4, ml: -1, px: 1, color: BRAND.ink, '&:hover': { backgroundColor: BRAND.hover } }}
          >
            See how Joan works
          </Button>
        </Box>

        <Box sx={{ flex: { xs: 0, md: PHI } }} />
      </Box>

      <HowItWorks />
      <SiteFooter />

      <SafetyGuidanceDialog
        open={openSafetyModal}
        onClose={handleCloseSafetyModal}
        confirmed={safetyConfirmed}
        onConfirmedChange={handleSafetyConfirmation}
        readOnly={isAuthenticated}
      />
    </Box>
  );
};

export default Landing;
